import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Calendar, Users, Download, FileText, DollarSign, Loader2, Baby, User, ChevronDown, ChevronUp, Save, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatDisplayDate } from "../../utils/dateFormat";
import TemplateEditorAccordion from "./proposal-builder/TemplateEditorAccordion";
import { generateProposalPDF, generateFilename } from "./proposal-builder/pdfGenerator";
import { DEFAULT_TEMPLATE_SETTINGS, mergeWithDefaults, migrateOldSettings } from "./proposal-builder/templateDefaults";
import { PageInfoInline } from "./PageInfo";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Debounce hook
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);
  const callbackRef = useRef(callback);
  
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callbackRef.current(...args);
    }, delay);
  }, [delay]);
}

export default function PreventivoPage({ getAuthHeaders }) {
  
  // Collapsible state for PDF Template Settings
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  
  // Settings from backend (defaults)
  const [settings, setSettings] = useState({
    extra_guest_fee: 30,
    cleaning_fee: 80,
    tourist_tax_per_person: 6,
    tourist_tax_max_nights: 10,
    tourist_tax_exemption_age: 10,
    base_price: 150,
    base_guests: 2
  });
  
  // Template settings (NEW: single JSON object)
  const [templateSettings, setTemplateSettings] = useState(DEFAULT_TEMPLATE_SETTINGS);
  const [saveStatus, setSaveStatus] = useState(null);
  
  // Form state (resets on refresh - non persistent)
  const [formData, setFormData] = useState({
    name: "",
    checkIn: "",
    checkOut: "",
    adults: 2,
    children: 0,
    infants: 0,
    childrenAges: [],
    priceOverride: "",
    extraGuestFeeOverride: "",
    cleaningFeeOverride: "",
    cityTaxOverride: "",
    taxNightsCapOverride: "",
    discount: 0
  });
  
  // Nightly rates from calendar
  const [nightlyRates, setNightlyRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Autosave with debounce
  const saveTemplateSettings = useCallback(async (newSettings) => {
    try {
      setSaveStatus('saving');
      await axios.put(`${API}/admin/preventivo/template-settings`, 
        { template_settings: newSettings }, 
        getAuthHeaders()
      );
      setSaveStatus('saved');
      // Clear status after 2 seconds
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error("Error saving template settings:", error);
      setSaveStatus('error');
      // Retry after 3 seconds on error
      setTimeout(() => saveTemplateSettings(newSettings), 3000);
    }
  }, [getAuthHeaders]);
  
  const debouncedSave = useDebounce(saveTemplateSettings, 600);
  
  // Handle template settings change with autosave
  const handleTemplateSettingsChange = (newSettings) => {
    setTemplateSettings(newSettings);
    debouncedSave(newSettings);
  };

  // Load settings on mount
  useEffect(() => {
    loadSettings();
    loadTemplateSettings();
  }, []);

  // Load nightly rates when dates change
  useEffect(() => {
    if (formData.checkIn && formData.checkOut) {
      loadNightlyRates();
    }
  }, [formData.checkIn, formData.checkOut]);

  // Update children ages array when count changes
  useEffect(() => {
    setFormData(prev => {
      const newAges = [...prev.childrenAges];
      while (newAges.length < prev.children) newAges.push("");
      while (newAges.length > prev.children) newAges.pop();
      return { ...prev, childrenAges: newAges };
    });
  }, [formData.children]);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/calendar/config`, getAuthHeaders());
      setSettings({
        extra_guest_fee: res.data.extra_guest_fee || 30,
        cleaning_fee: res.data.cleaning_fee || 80,
        tourist_tax_per_person: res.data.tourist_tax_per_person || 6,
        tourist_tax_max_nights: res.data.tourist_tax_max_nights || 10,
        tourist_tax_exemption_age: res.data.tourist_tax_exemption_age || 10,
        base_price: res.data.base_price || 150,
        base_guests: res.data.base_guests || 2
      });
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/preventivo/template-settings`, getAuthHeaders());
      if (res.data.template_settings) {
        // New format exists
        setTemplateSettings(mergeWithDefaults(res.data.template_settings));
      } else if (res.data.migrated) {
        // Backend migrated old settings
        setTemplateSettings(mergeWithDefaults(res.data.migrated));
      } else {
        // Use defaults
        setTemplateSettings(DEFAULT_TEMPLATE_SETTINGS);
      }
    } catch (error) {
      console.error("Error loading template settings:", error);
      setTemplateSettings(DEFAULT_TEMPLATE_SETTINGS);
    }
  };

  const loadNightlyRates = async () => {
    try {
      const res = await axios.get(`${API}/admin/calendar/prices`, getAuthHeaders());
      const prices = res.data || [];
      
      const start = new Date(formData.checkIn);
      const end = new Date(formData.checkOut);
      const rates = [];
      
      let current = new Date(start);
      while (current < end) {
        const dateStr = current.toISOString().split("T")[0];
        const priceEntry = prices.find(p => dateStr >= p.start_date && dateStr <= p.end_date);
        const rate = priceEntry ? priceEntry.price_per_night : settings.base_price;
        rates.push({ date: dateStr, rate });
        current.setDate(current.getDate() + 1);
      }
      
      setNightlyRates(rates);
    } catch (error) {
      console.error("Error loading rates:", error);
    }
  };

  // Calculate nights
  const nights = formData.checkIn && formData.checkOut
    ? Math.ceil((new Date(formData.checkOut) - new Date(formData.checkIn)) / (1000 * 60 * 60 * 24))
    : 0;

  // Get effective values (override or default)
  const effectiveExtraGuestFee = formData.extraGuestFeeOverride !== "" 
    ? parseFloat(formData.extraGuestFeeOverride) 
    : settings.extra_guest_fee;
  
  const effectiveCleaningFee = formData.cleaningFeeOverride !== "" 
    ? parseFloat(formData.cleaningFeeOverride) 
    : settings.cleaning_fee;
  
  const effectiveCityTax = formData.cityTaxOverride !== "" 
    ? parseFloat(formData.cityTaxOverride) 
    : settings.tourist_tax_per_person;
  
  const effectiveTaxNightsCap = formData.taxNightsCapOverride !== "" 
    ? parseInt(formData.taxNightsCapOverride) 
    : settings.tourist_tax_max_nights;

  // Calculate accommodation
  const accommodationTotal = formData.priceOverride !== "" && parseFloat(formData.priceOverride) > 0
    ? nights * parseFloat(formData.priceOverride)
    : nightlyRates.reduce((sum, r) => sum + r.rate, 0);

  // Count extra guests (adults + children age > 1) - ABOVE base guests included
  const childrenOver1 = formData.childrenAges.filter(age => age && parseInt(age) > 1).length;
  const eligibleGuests = formData.adults + childrenOver1;
  const baseGuestsIncluded = settings.base_guests || 2;
  const extraGuestCount = Math.max(0, eligibleGuests - baseGuestsIncluded);
  
  // Extra guest fee total
  const extraGuestFeeTotal = extraGuestCount * effectiveExtraGuestFee * nights;

  // City tax calculation
  const taxableChildren = formData.childrenAges.filter(age => age && parseInt(age) > settings.tourist_tax_exemption_age).length;
  const taxablePeople = formData.adults + taxableChildren;
  const taxableNights = Math.min(nights, effectiveTaxNightsCap);
  const cityTaxTotal = taxablePeople * effectiveCityTax * taxableNights;

  // Discount
  const discountAmount = parseFloat(formData.discount) || 0;

  // Subtotal before tax
  const subtotalBeforeTax = accommodationTotal + extraGuestFeeTotal + effectiveCleaningFee - discountAmount;

  // Grand total
  const grandTotal = subtotalBeforeTax + cityTaxTotal;

  // Generate PDF using new system
  const generatePDF = async () => {
    setGenerating(true);
    try {
      // Build extra guest labels
      const ordinals = ["Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth"];
      const extraGuestLabels = [];
      for (let i = 0; i < extraGuestCount; i++) {
        const ordinal = ordinals[i] || `Guest ${i + 3}`;
        extraGuestLabels.push(`${ordinal} guest €${effectiveExtraGuestFee}`);
      }
      
      // Build nightly rates with totals
      const nightlyRatesWithTotals = nightlyRates.map(nr => {
        const nightRateValue = formData.priceOverride !== "" && parseFloat(formData.priceOverride) > 0 
          ? parseFloat(formData.priceOverride) 
          : nr.rate;
        return {
          date: nr.date,
          rate: nightRateValue,
          total: nightRateValue + (extraGuestCount * effectiveExtraGuestFee)
        };
      });
      
      // Build quote_data
      const quoteData = {
        guestName: formData.name || "",
        checkIn: formData.checkIn,
        checkOut: formData.checkOut,
        nights: nights,
        adults: formData.adults,
        children: formData.children,
        infants: formData.infants,
        nightlyRates: nightlyRatesWithTotals,
        extraGuestCount: extraGuestCount,
        extraGuestLabels: extraGuestLabels,
        extraGuestFeePerNight: effectiveExtraGuestFee,
        extraGuestFeeTotal: extraGuestFeeTotal,
        accommodationTotal: accommodationTotal,
        cleaningFee: effectiveCleaningFee,
        cityTaxRate: effectiveCityTax,
        cityTaxTotal: cityTaxTotal,
        taxablePeople: taxablePeople,
        taxableNights: taxableNights,
        subtotal: subtotalBeforeTax,
        discount: discountAmount,
        total: grandTotal
      };
      
      // Generate PDF
      const doc = await generateProposalPDF(templateSettings, quoteData);
      const filename = generateFilename(quoteData);
      
      // Download
      doc.save(filename);
      toast.success("Proposal generated successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error generating proposal");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl">Proposal</h1>
        <PageInfoInline pageKey="proposal" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column - Input Form */}
        <div className="space-y-6">
          {/* Guest & Dates */}
          <div className="admin-card">
            <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
              <Calendar size={20} className="text-[#D4AF37]" />
              Guest & Dates
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-[#78716C] mb-1 block">Name (optional)</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Guest name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#78716C] mb-1 block">Check-in</label>
                  <Input
                    type="date"
                    value={formData.checkIn}
                    onChange={(e) => setFormData({ ...formData, checkIn: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm text-[#78716C] mb-1 block">Check-out</label>
                  <Input
                    type="date"
                    value={formData.checkOut}
                    onChange={(e) => setFormData({ ...formData, checkOut: e.target.value })}
                  />
                </div>
              </div>

              {nights > 0 && (
                <div className="p-3 bg-[#F9F8F6] rounded-lg">
                  <p className="text-sm"><span className="text-[#78716C]">Nights:</span> <span className="font-medium">{nights}</span></p>
                </div>
              )}
            </div>
          </div>

          {/* Occupants */}
          <div className="admin-card">
            <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
              <Users size={20} className="text-[#D4AF37]" />
              Occupants
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-[#78716C] mb-1 block">Adults</label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.adults === 2 ? "" : formData.adults}
                    onChange={(e) => setFormData({ ...formData, adults: e.target.value === "" ? 2 : parseInt(e.target.value) || 1 })}
                    onBlur={(e) => { if (e.target.value === "") setFormData(prev => ({...prev, adults: 2})); }}
                    placeholder="2"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#78716C] mb-1 block">Children (+1Y)</label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.children === 0 ? "" : formData.children}
                    onChange={(e) => setFormData({ ...formData, children: e.target.value === "" ? 0 : parseInt(e.target.value) || 0 })}
                    onBlur={(e) => { if (e.target.value === "") setFormData(prev => ({...prev, children: 0})); }}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#78716C] mb-1 block">Infants (0Y)</label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.infants === 0 ? "" : formData.infants}
                    onChange={(e) => setFormData({ ...formData, infants: e.target.value === "" ? 0 : parseInt(e.target.value) || 0 })}
                    onBlur={(e) => { if (e.target.value === "") setFormData(prev => ({...prev, infants: 0})); }}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Children ages */}
              {formData.children > 0 && (
                <div className="p-3 bg-[#F9F8F6] rounded-lg">
                  <label className="text-sm text-[#78716C] mb-2 block">Children Ages (years)</label>
                  <div className="grid grid-cols-4 gap-2">
                    {formData.childrenAges.map((age, index) => (
                      <div key={index} className="flex items-center gap-1">
                        <span className="text-xs text-[#78716C]">C{index + 1}:</span>
                        <select
                          value={age}
                          onChange={(e) => {
                            const newAges = [...formData.childrenAges];
                            newAges[index] = e.target.value;
                            setFormData({ ...formData, childrenAges: newAges });
                          }}
                          className="flex-1 px-2 py-1 text-sm border border-[#E7E5E4] rounded"
                        >
                          <option value="">--</option>
                          {Array.from({ length: 17 }, (_, i) => i + 1).map(a => (
                            <option key={a} value={a}>{a}Y</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Fees Override */}
          <div className="admin-card">
            <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
              <DollarSign size={20} className="text-[#D4AF37]" />
              Fees (Override)
            </h2>
            <p className="text-xs text-[#78716C] mb-4">Leave empty to use defaults from Settings. Changes here do NOT persist.</p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#78716C] mb-1 block">Price per night (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.priceOverride}
                  onChange={(e) => setFormData({ ...formData, priceOverride: e.target.value })}
                  placeholder={`Default from calendar`}
                />
              </div>
              <div>
                <label className="text-sm text-[#78716C] mb-1 block">Extra guest fee (€/night)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.extraGuestFeeOverride}
                  onChange={(e) => setFormData({ ...formData, extraGuestFeeOverride: e.target.value })}
                  placeholder={`Default: €${settings.extra_guest_fee}`}
                />
              </div>
              <div>
                <label className="text-sm text-[#78716C] mb-1 block">Cleaning fee (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cleaningFeeOverride}
                  onChange={(e) => setFormData({ ...formData, cleaningFeeOverride: e.target.value })}
                  placeholder={`Default: €${settings.cleaning_fee}`}
                />
              </div>
              <div>
                <label className="text-sm text-[#78716C] mb-1 block">City tax (€/person/night)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.cityTaxOverride}
                  onChange={(e) => setFormData({ ...formData, cityTaxOverride: e.target.value })}
                  placeholder={`Default: €${settings.tourist_tax_per_person}`}
                />
              </div>
              <div>
                <label className="text-sm text-[#78716C] mb-1 block">Max nights for tax</label>
                <Input
                  type="number"
                  value={formData.taxNightsCapOverride}
                  onChange={(e) => setFormData({ ...formData, taxNightsCapOverride: e.target.value })}
                  placeholder={`Default: ${settings.tourist_tax_max_nights}`}
                />
              </div>
              <div>
                <label className="text-sm text-[#78716C] mb-1 block">Special Offer (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right column - Preview & Generate */}
        <div className="space-y-6">
          {/* Stay Details - Nightly Breakdown (ONE ROW PER NIGHT) */}
          {nights > 0 && nightlyRates.length > 0 && (
            <div className="admin-card border-l-4 border-l-[#D4AF37]">
              <h2 className="font-heading text-lg mb-4">Stay Details</h2>
              
              {/* Nightly Details - One row per night */}
              <div className="space-y-1 text-sm font-mono">
                {nightlyRates.map((nightRate, idx) => {
                  const dateStr = formatDisplayDate(nightRate.date);
                  const nightRateValue = formData.priceOverride !== "" && parseFloat(formData.priceOverride) > 0 
                    ? parseFloat(formData.priceOverride) 
                    : nightRate.rate;
                  
                  const extraGuestLabels = [];
                  for (let i = 0; i < extraGuestCount; i++) {
                    const ordinal = ["Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth"][i] || `Guest ${i + 3}`;
                    extraGuestLabels.push(`${ordinal} guest €${effectiveExtraGuestFee}`);
                  }
                  
                  const infantLabels = [];
                  for (let i = 0; i < formData.infants; i++) {
                    infantLabels.push("Infant (0Y) €0");
                  }
                  
                  const rowTotal = nightRateValue + (extraGuestCount * effectiveExtraGuestFee);
                  const parts = [`Night rate €${nightRateValue}`];
                  parts.push(...extraGuestLabels);
                  parts.push(...infantLabels);
                  
                  return (
                    <div key={idx} className="text-[#44403C] py-1 border-b border-[#F5F5F4]">
                      <span className="text-[#78716C]">{dateStr}</span> — {parts.join(" + ")} = <span className="font-semibold">€{rowTotal}</span>
                    </div>
                  );
                })}
              </div>
              
              {/* One-time fees below nightly details */}
              <div className="mt-4 pt-4 border-t border-[#E7E5E4] space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#78716C]">Cleaning fee (one-time)</span>
                  <span className="font-medium">€{effectiveCleaningFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#78716C]">City tax ({taxablePeople} persons × €{effectiveCityTax} × {taxableNights} nights)</span>
                  <span className="font-medium">€{cityTaxTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Price Breakdown with Formulas */}
          <div className="admin-card border-l-4 border-l-[#D4AF37]">
            <h2 className="font-heading text-lg mb-4">Price Breakdown</h2>
            
            {nights > 0 ? (
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-[#E7E5E4]">
                  <span className="text-[#78716C]">Accommodation ({nights} nights × €{(accommodationTotal / nights).toFixed(2)} avg)</span>
                  <span className="font-medium">€{accommodationTotal.toFixed(2)}</span>
                </div>
                
                {extraGuestFeeTotal > 0 && (
                  <div className="flex justify-between py-2 border-b border-[#E7E5E4]">
                    <span className="text-[#78716C]">Extra Guest Fee ({extraGuestCount} guest{extraGuestCount > 1 ? "s" : ""} × €{effectiveExtraGuestFee} × {nights} nights)</span>
                    <span className="font-medium">€{extraGuestFeeTotal.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between py-2 border-b border-[#E7E5E4]">
                  <span className="text-[#78716C]">Cleaning Fee</span>
                  <span className="font-medium">€{effectiveCleaningFee.toFixed(2)}</span>
                </div>
                
                {discountAmount > 0 && (
                  <div className="flex justify-between py-2 border-b border-[#E7E5E4] text-green-600">
                    <span>Special Offer</span>
                    <span className="font-medium">-€{discountAmount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between py-2 border-b border-[#E7E5E4]">
                  <span className="text-[#78716C]">City Tax ({taxablePeople} persons × €{effectiveCityTax} × {taxableNights} nights)</span>
                  <span className="font-medium">€{cityTaxTotal.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between py-3 bg-[#F9F8F6] rounded-lg px-3 mt-4">
                  <span className="font-heading text-lg">Total</span>
                  <span className="font-heading text-xl text-[#D4AF37]">€{grandTotal.toFixed(2)}</span>
                </div>

                <div className="text-xs text-[#78716C] space-y-1 mt-4">
                  <p>• Base guests included: {baseGuestsIncluded} | Eligible: {eligibleGuests} | Extra: {extraGuestCount}</p>
                </div>
              </div>
            ) : (
              <p className="text-[#78716C] text-center py-8">Select dates to see price breakdown</p>
            )}
          </div>

          {/* PDF Template Configuration - NEW Accordion UI */}
          <div className="admin-card">
            <div className="border border-[#D4AF37] rounded-lg overflow-hidden">
              {/* Header - clickable to toggle */}
              <button
                type="button"
                onClick={() => setTemplateEditorOpen(!templateEditorOpen)}
                className="w-full flex items-center justify-between p-3 bg-[#FFFDF7] hover:bg-[#FDF9EF] transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText size={20} className="text-[#D4AF37]" />
                  <h2 className="font-heading text-lg">PDF Template Settings</h2>
                </div>
                <div className="flex items-center gap-2">
                  {saveStatus === 'saved' && (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 size={12} /> Saved
                    </span>
                  )}
                  {saveStatus === 'saving' && (
                    <span className="text-xs text-yellow-600 flex items-center gap-1">
                      <Loader2 size={12} className="animate-spin" /> Saving...
                    </span>
                  )}
                  {templateEditorOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </button>
              
              {/* Collapsible content */}
              {templateEditorOpen && (
                <div className="p-4 bg-white border-t border-[#E7E5E4]">
                  <p className="text-xs text-[#78716C] mb-4">All settings are saved automatically (autosave). No save button needed.</p>
                  <TemplateEditorAccordion 
                    settings={templateSettings}
                    onSettingsChange={handleTemplateSettingsChange}
                    saveStatus={saveStatus}
                    getAuthHeaders={getAuthHeaders}
                    guestName={formData.name}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Generate Button */}
          <Button
            onClick={generatePDF}
            disabled={!formData.checkIn || !formData.checkOut || nights <= 0 || generating}
            className="w-full bg-[#D4AF37] hover:bg-[#B8972E] text-white py-4 text-lg"
          >
            {generating ? (
              <>
                <Loader2 className="animate-spin mr-2" size={20} />
                Generating...
              </>
            ) : (
              <>
                <Download className="mr-2" size={20} />
                Generate Proposal (PDF)
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
